import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import axios from 'axios';
import { Download } from 'lucide-react';
import { getApiBaseUrl } from '../../services/api';
import { Action } from '../../components/aa';

/*
 * The consultant plugin, pinned to one client. The zip carries a freshly
 * minted credential scoped to them, so it is a per-client download rather than
 * one file the partner keeps. It sits in the page header wherever a client is
 * in view.
 *
 * Whether this partner may download at all is a platform setting the server
 * re-checks on every download. The button asks once per session so it can say
 * so up front, disabled with the reason, instead of failing on click.
 */

let accessPromise: Promise<boolean> | null = null;

const pluginAccess = (): Promise<boolean> => {
  if (!accessPromise) {
    accessPromise = axios
      .get<{ allowed: boolean }>(`${getApiBaseUrl()}/plugin/access`)
      .then((res) => Boolean(res.data?.allowed))
      .catch(() => false);
  }
  return accessPromise;
};

const NOT_ENABLED = 'Orbit downloads are not enabled for your account. Ask the platform team to switch them on.';

export const DownloadOrbit: React.FC<{ clientId: string; clientName: string }> = ({ clientId, clientName }) => {
  const [busy, setBusy] = useState(false);
  const [allowed, setAllowed] = useState<boolean | null>(null);

  useEffect(() => {
    let live = true;
    pluginAccess().then((ok) => {
      if (live) setAllowed(ok);
    });
    return () => { live = false; };
  }, []);

  const download = async () => {
    setBusy(true);
    try {
      const url = `${getApiBaseUrl()}/plugin/download?client_id=${encodeURIComponent(clientId)}`;
      const res = await axios.get(url, { responseType: 'blob' });
      const disposition = res.headers['content-disposition'] || '';
      const named = disposition.match(/filename="?([^";]+)"?/);
      const href = URL.createObjectURL(res.data);
      const link = document.createElement('a');
      link.href = href;
      link.download = named?.[1] || 'orbit-scope.zip';
      link.click();
      URL.revokeObjectURL(href);
      toast.success(`Downloaded, pinned to ${clientName}. Any earlier copy is now revoked.`);
    } catch (err) {
      toast.error(await failureMessage(err));
    } finally {
      setBusy(false);
    }
  };

  if (allowed === null) {
    return (
      <Action variant="quiet" icon={<Download size={14} />} disabled reason="Checking your Orbit download access">
        Download Orbit
      </Action>
    );
  }
  if (!allowed) {
    return (
      <Action variant="quiet" icon={<Download size={14} />} disabled reason={NOT_ENABLED}>
        Download Orbit
      </Action>
    );
  }
  return (
    <Action variant="quiet" icon={<Download size={14} />} onClick={download} disabled={busy} reason="Preparing the download">
      Download Orbit
    </Action>
  );
};

// The server answers a failed download with a sentence; a blob response hides
// it, so read it back before falling to the generic line.
const failureMessage = async (err: unknown): Promise<string> => {
  const fallback = 'Could not download Orbit. Try again, or ask the platform team.';
  if (!axios.isAxiosError(err) || !(err.response?.data instanceof Blob)) return fallback;
  try {
    const parsed = JSON.parse(await err.response.data.text()) as { detail?: string };
    return parsed.detail || fallback;
  } catch {
    return fallback;
  }
};
